// apps/client/src/hooks/useAggregationStatus.ts
// Detects when commission data is being (re)aggregated for a company.
// Consumers: stat-card "updating" shimmer (Dashboard/Office pages) and the
// dashboard query-cache freshness rule (QueryCacheLifecycle resets cached
// dashboard queries on the aggregating -> idle transition).
//
// TWO signal sources, OR-combined (gap discovered 2026-07-11 during the
// Tier 3 stage soak — see docs/handovers/2026-07-11-LOADING-UX-PROGRAM-HANDOVER.md):
//
// 1. companies/{id}/aggregation_triggers — LEGACY/REBUILD paths only. The
//    standard upload pipeline stopped writing these when it moved to
//    BigQuery-incremental (processAndWrite.ts "Removed code" note), so on
//    normal uploads this collection stays silent and this source alone made
//    the hook a dead signal for the most common case.
// 2. client_processing_queue — the LIVE signal for standard uploads. The
//    pipeline stamps status 'completed' on the queue doc AFTER incremental
//    aggregation finishes, so a pending/processing doc here means the
//    dashboards are about to change. Query shape follows UploadPage.tsx
//    (companyId ==, status IN, orderBy uploadTimestamp); the 2h recency
//    floor keeps a stuck/abandoned queue doc from pinning the signal on
//    forever.

import { useState, useEffect } from 'react';
import { collection, query, where, orderBy, limit, onSnapshot, Timestamp } from 'firebase/firestore';
import { db } from '../firebase';

/** Ignore queue docs older than this — stuck uploads must not pin the signal. */
const QUEUE_RECENCY_WINDOW_MS = 2 * 60 * 60 * 1000;

export function useAggregationStatus(companyId: string) {
  const [hasActiveTrigger, setHasActiveTrigger] = useState(false);
  const [hasActiveUpload, setHasActiveUpload] = useState(false);

  // Source 1: legacy/rebuild aggregation triggers (behavior unchanged).
  useEffect(() => {
    if (!companyId) {
      setHasActiveTrigger(false);
      return;
    }

    const triggersQuery = query(
      collection(db, `companies/${companyId}/aggregation_triggers`),
      where('status', 'in', ['pending', 'in_progress']),
      orderBy('createdAt', 'desc'),
      limit(1)
    );

    const unsubscribe = onSnapshot(
      triggersQuery,
      (snapshot) => {
        setHasActiveTrigger(!snapshot.empty);

        if (!snapshot.empty) {
          const trigger = snapshot.docs[0].data();
          console.log('[AGGREGATION_STATUS] Rebuild aggregation in progress:', {
            type: trigger.type,
            status: trigger.status,
            createdAt: trigger.createdAt
          });
        }
      },
      (error) => {
        console.error('[AGGREGATION_STATUS] Error monitoring aggregation triggers:', error);
        setHasActiveTrigger(false);
      }
    );

    return () => unsubscribe();
  }, [companyId]);

  // Source 2: standard upload pipeline (client_processing_queue). A doc in
  // pending/processing means processAndWrite has not yet finished incremental
  // aggregation for it; the doc flipping to completed/failed drops it from
  // this query, transitioning the combined signal to idle.
  useEffect(() => {
    if (!companyId) {
      setHasActiveUpload(false);
      return;
    }

    const recencyFloor = Timestamp.fromMillis(Date.now() - QUEUE_RECENCY_WINDOW_MS);
    const queueQuery = query(
      collection(db, 'client_processing_queue'),
      where('companyId', '==', companyId),
      where('status', 'in', ['pending', 'processing']),
      where('uploadTimestamp', '>', recencyFloor),
      orderBy('uploadTimestamp', 'desc'),
      limit(1)
    );

    const unsubscribe = onSnapshot(
      queueQuery,
      (snapshot) => {
        setHasActiveUpload(!snapshot.empty);

        if (!snapshot.empty) {
          const queueDoc = snapshot.docs[0].data();
          console.log('[AGGREGATION_STATUS] Upload processing in progress:', {
            status: queueDoc.status,
            fileName: queueDoc.originalFileName ?? queueDoc.fileName,
          });
        }
      },
      (error) => {
        // Fail-open to "not aggregating": the shimmer/reset degrade to the
        // pre-fix behavior, and this error line is the loud diagnostic.
        console.error('[AGGREGATION_STATUS] Error monitoring processing queue:', error);
        setHasActiveUpload(false);
      }
    );

    return () => unsubscribe();
  }, [companyId]);

  return hasActiveTrigger || hasActiveUpload;
}
